import { TextToSpeechService } from "../../../../services/TextToSpeechService";
import React, { useEffect, useRef, useState } from "react";
import { TextToSpeechContext as ReactTextToSpeechContext } from "./useTextToSpeech";
import { SpeechSynthesizer, AudioConfig, SpeechConfig, SpeechSynthesisResult, SpeechSynthesisOutputFormat, AudioOutputStream } from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/microsoft.cognitiveservices.speech.sdk';
import { TextToSpeechSettings } from './TextToSpeechSettings';
import { ServerResponse } from '../../../../services/server/OldWebClient';
import { useEditor } from '../useEditor';
import { useBuilder } from '../../useBuilder';
import { EventTracking } from '../../../../utilities/Tracking/EventTracking'
import { BuilderPreferences } from "../../../../entities/BuilderPreferences";
import { UserAgent } from "../../../../utilities/UserAgent";

interface Props{}

function TextToSpeechContext(props: React.PropsWithChildren<Props>) {
    const builder = useBuilder();
    const {user, setAlert, preferences, savePreferences } = builder;
    const editor = useEditor();
    const isMobile = UserAgent.mobile();
    const audioPlayerRef = useRef<HTMLAudioElement>(null);

    const [bubbleWordCount, setBubbleWordCount] = useState<number>(0);
    const [showAudioPlayer, setShowAudioPlayer] = useState<boolean>(false);
    
    const [audioFileUrl, setAudioFileURL] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isDisabled, setIsDisabled] = useState<boolean>(false)
    const [isAudioPaused, setIsAudioPaused] = useState<boolean>(true);

    const [synthesizerVoice, setSynthesizerVoice] = useState<string>(preferences.textToSpeechVoice);
    const [synthesizerSpeed, setSynthesizerSpeed] = useState<number>(preferences.textToSpeechSpeed);
    
    const [openSettings, setOpenSettings] = useState<boolean>(false);

    useEffect(() => {
        setSynthesizerVoice(preferences.textToSpeechVoice ? preferences.textToSpeechVoice : "en-US-BenjaminRUS");
        setSynthesizerSpeed(preferences.textToSpeechSpeed);
    }, [preferences])

    const startTextToSpeech = async(textToSpeechVoice:string = synthesizerVoice, textToSpeechSpeed:number = synthesizerSpeed) => {
        if(user && !user.makerSubscription){
            return window.location.assign(`https://www.sermonmaker.net/upgrade-now/?upgradePath=HearItNow&upgradeAction=16`);
        }

        const audioPlayer = audioPlayerRef.current!;
            
        if(!audioPlayer.paused){
            audioPlayer.pause()
            setIsAudioPaused(true)
        }

        const wordCount = editor.getBubbleWordCount() 
        
        if(wordCount > 1000){
            return setAlert("Unable to process, text too long");
        }

        try {
            editor.hideFormatBubble()
            const text:string = editor.getBubbleText();
            setShowAudioPlayer(true);
            setIsLoading(true)
                            
            let conversion = `<speak version='1.0' xmlns="http://www.w3.org/2001/10/synthesis" xml:lang='en-US'>
            <voice  name="${textToSpeechVoice}">
            <prosody rate="${textToSpeechSpeed}">
                ${text}
            </prosody>
            </voice>
            </speak>`
            
            let token = await TextToSpeechService.getToken();

            if(ServerResponse.isSuccess(token)){
                let speechConfig = SpeechConfig.fromAuthorizationToken(token.message, "westus2");
                speechConfig.speechSynthesisOutputFormat = SpeechSynthesisOutputFormat.Audio24Khz160KBitRateMonoMp3;

                const stream = AudioOutputStream.createPullStream() ;
                const audioConfig = AudioConfig.fromStreamOutput(stream);

                let speechSynthesizer = new SpeechSynthesizer(speechConfig, audioConfig);

                speechSynthesizer.speakSsmlAsync(conversion, (result:SpeechSynthesisResult) => {
                    speechSynthesizer.close();
                    EventTracking.Builder.onStartedTextToSpeech(0, wordCount)
                    
                    let audioFile = URL.createObjectURL(new Blob([result.audioData], {type: 'audio/mpeg'}));
                    
                    if(!isMobile){
                        audioPlayer.autoplay = true;
                        setIsAudioPaused(false);
                    }
                    
                    audioPlayer.onended = stopTextToSpeech;
                    audioPlayer.src = audioFile;
                    audioPlayer.load();
                    
                    setIsLoading(false);
                    setBubbleWordCount(wordCount);
                    setAudioFileURL(audioFile);
                }, (e) => {
                    setShowAudioPlayer(false);
                    setAlert(e);
            })}
        } catch(e){
            setAlert("Something went wrong, please try again.");
        }
    };
    
    const pauseTextToSpeech = () => {
        const audioPlayer = audioPlayerRef.current!;
        EventTracking.Builder.onPausedTextToSpeech();
        setIsAudioPaused(true);
        audioPlayer.pause();
    }
    
    const resumeTextToSpeech = () => {
        const audioPlayer = audioPlayerRef.current!;

        EventTracking.Builder.onUnPausedTextToSpeech();
        audioPlayer.play();
        setIsAudioPaused(false);
    }
    
    const restartTextToSpeech = () => {
        const audioPlayer = audioPlayerRef.current!;
        EventTracking.Builder.onReStartTextToSpeech();
        audioPlayer.pause();
        audioPlayer.currentTime = 0;
        setIsAudioPaused(false);
        audioPlayer.play();
    }

    const stopTextToSpeech = () => {
        const audioPlayer = audioPlayerRef.current!;
        EventTracking.Builder.onCompletedTextToSpeech(audioPlayer.currentTime, bubbleWordCount);
        audioPlayer.pause();
        setShowAudioPlayer(false);
        setIsAudioPaused(true)
        URL.revokeObjectURL(audioFileUrl);
    }

    const handlePreferenceChange = (voice:string, speed:number) => {
        setIsDisabled(true);
        setOpenSettings(!openSettings);
        const audioPlayer = audioPlayerRef.current!;

        audioPlayer.pause();
        
        let updatedPreferences:BuilderPreferences = {...preferences};
        
        if(voice !== preferences.textToSpeechVoice){
            EventTracking.Builder.onChangeTextToSpeechVoice(voice);
            updatedPreferences.textToSpeechVoice = voice;
            setSynthesizerVoice(voice);
        }
        
        if(speed !== preferences.textToSpeechSpeed){
            EventTracking.Builder.onChangeTextToSpeechSpeed(speed);
            updatedPreferences.textToSpeechSpeed = speed;
            setSynthesizerSpeed(speed);
        }

        savePreferences(updatedPreferences);
        setAlert("Preferences Saved");
        setIsDisabled(false);
        startTextToSpeech(voice, speed);
    }

    const toggleSettingsOpen = () => setOpenSettings(!openSettings);

	return (
        <ReactTextToSpeechContext.Provider 
            value={{
                showAudioPlayer: showAudioPlayer,
                isAudioPaused: isAudioPaused,
                isLoading: isLoading,
                toggleSettingsOpen: toggleSettingsOpen,
                pauseTextToSpeech: pauseTextToSpeech,
                startTextToSpeech: startTextToSpeech,
                resumeTextToSpeech: resumeTextToSpeech,
                stopTextToSpeech: stopTextToSpeech,
                restartTextToSpeech: restartTextToSpeech,
            }}>
            { props.children }
            <TextToSpeechSettings  
                toggleSettingsOpen={toggleSettingsOpen}
                disabled={isDisabled}
                showSettings={openSettings} 
                handlePreferenceChange={handlePreferenceChange}/>
            <audio ref={audioPlayerRef} />
        </ReactTextToSpeechContext.Provider>
	)
}

export default (TextToSpeechContext);